import { AuthedAxiosRequests } from 'features/auth'
import { extractPlainText, RichTextContent } from 'types'

/**
 * Replaces empty Lexical mention spans with their display text before plain-text
 * extraction. Mention nodes serialize to `<span data-lexical-mention="true" ...>`
 * with no inner text (the text is rendered via React's decorate()), so a raw
 * textContent read would silently drop the name.
 */
function inlineMentionText(html: string): string {
  return html.replace(
    /<span[^>]*data-lexical-mention="true"[^>]*data-user-name="([^"]*)"[^>]*>.*?<\/span>/gs,
    '@$1'
  )
}

const BULK_NOTIFICATIONS_PATH = '/api/v1/notifications/bulk'

interface CreateCommentNotificationsParams {
  text: RichTextContent
  ticketTitle: string
  subtitle?: string
  organizationId: string
  recipientUserIds: string[]
  authorId: string
  commentAuthorName?: string
  alertsUrl: string
  axios: AuthedAxiosRequests
  ticketUrl?: string
}

/**
 * Sends an in-app notification to all users on a comment thread (excluding the author).
 * Fire-and-forget: errors are logged but do not propagate.
 */
export async function createCommentNotifications({
  text,
  ticketTitle,
  subtitle,
  organizationId,
  recipientUserIds,
  authorId,
  commentAuthorName,
  alertsUrl,
  axios,
  ticketUrl,
}: CreateCommentNotificationsParams): Promise<void> {
  if (!alertsUrl || !organizationId) return

  const targets = recipientUserIds.filter((id) => id !== authorId)
  if (targets.length === 0) return

  const plainText = extractPlainText(inlineMentionText(text) as RichTextContent)
  const body = plainText
    ? plainText.length <= 120
      ? plainText
      : plainText.substring(0, plainText.lastIndexOf(' ', 120) || 120) + '...'
    : 'A new comment was posted.'

  try {
    await axios.post(
      `${alertsUrl}${BULK_NOTIFICATIONS_PATH}`,
      {
        type: 'libra.comment.created',
        user_ids: targets,
        from_user_id: authorId,
        organization_id: organizationId,
        channels: ['inapp'],
        payload: {
          title: commentAuthorName
            ? `New comment by ${commentAuthorName} on "${ticketTitle}"`
            : `New comment on "${ticketTitle}"`,
          body,
          ...(subtitle ? { subtitle } : {}),
          ...(ticketUrl
            ? { cta: { label: 'View comment', url: ticketUrl } }
            : {}),
        },
      },
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    )
  } catch (error) {
    console.error('Failed to send comment notifications:', error)
  }
}
